import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import LegiSIM from "./LegisSim_HomePage";
import DashboardPage from "./LegiSIM_Dashboard";
import PendingPage from "./LegiSIM_Pending";
import LegiSIMPlatform from "./LegiSIM_Platform";
import BillStudio from "./LegiSIM_BillStudio";
import SubscribePage from "./LegiSIM_Subscribe";
import FloorVoteSimulator from "./LegiSIM_FloorVote";
import LoginPage from "./LegiSIM_Login";
import AccountPage from "./LegiSIM_Account";
import CampaignFinancePage from "./LegiSIM_CampaignFinance";
import LegislativeCalendar from "./LegiSIM_Calendar";
import NewsPage from "./LegiSIM_News";
import DistrictMapPage from "./LegiSIM_Map";
import BillTrackerPage from "./LegiSIM_BillTracker";
import TrackedLegislationPage from "./LegiSIM_TrackedLegislation";
import ClientPortalPage from "./LegiSIM_ClientPortal";
import DirectoryPage from "./LegiSIM_Directory";
import DirectoryAdmin from "./LegiSIM_DirectoryAdmin";

const PROTECTED = ["platform", "billstudio", "floatvote", "finance", "account", "dashboard", "billtracker", "trackedlegislation", "clients", "calendar", "map", "news", "directory"];
const VALID_PAGES = ["home", "platform", "billstudio", "floatvote", "finance", "calendar", "news", "map", "billtracker", "trackedlegislation", "account", "subscribe", "dashboard", "login", "clients", "directory", "directoryadmin"];

function getHashPage() {
  const hash = window.location.hash.replace("#", "").toLowerCase();
  return VALID_PAGES.includes(hash) ? hash : "home";
}

export default function App() {
  const [page, setPage] = useState(getHashPage);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Listen for auth state changes
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) fetchProfile(session.user.id);
      setAuthLoading(false);
    });

    // Listen for sign in / sign out
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        fetchProfile(session.user.id);
        // After login, send to dashboard if on login/home page — don't disrupt other pages
        setPage(p => (p === "login" || p === "home") ? "dashboard" : p);
      } else {
        setProfile(null);
        setPage("home");
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId) => {
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (data) {
      // Preserve previous_last_login before overwriting so Dashboard can diff against it
      setProfile({ ...data, previous_last_login: data.last_login });
    }

    // Update last_login to now
    await supabase
      .from("profiles")
      .update({ last_login: new Date().toISOString() })
      .eq("id", userId);
  };

  // Sync hash -> page on back/forward
  useEffect(() => {
    const onPopState = () => setPage(getHashPage());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Sync page -> hash
  useEffect(() => {
    const newHash = page === "home" ? "" : page;
    if (window.location.hash.replace("#", "") !== newHash) {
      window.location.hash = newHash;
    }
  }, [page]);

  const handleNavigate = (dest) => {
    if (PROTECTED.includes(dest) && !session) {
      setPage("login");
      return;
    }
    setPage(dest);
  };

  // Show nothing while checking auth state
  if (authLoading) return null;

  // Auth props passed to pages that need them
  const authProps = { session, profile, onNavigate: handleNavigate };

  if (page === "login") return <LoginPage onNavigate={handleNavigate} />;

  // If logged in but not approved, show pending page (admin bypasses)
  if (session && profile && !profile.approved && profile.role !== "admin") {
    return <PendingPage onNavigate={handleNavigate} session={session} />;
  }
  if (page === "dashboard") return <DashboardPage {...authProps} />;
  if (page === "platform") return <LegiSIMPlatform {...authProps} />;
  if (page === "billstudio") return <BillStudio {...authProps} />;
  if (page === "subscribe") return <SubscribePage onNavigate={handleNavigate} />;
  if (page === "account") return <AccountPage {...authProps} />;
  if (page === "finance") return <CampaignFinancePage {...authProps} />;
  if (page === "floatvote") return <FloorVoteSimulator {...authProps} />;
  if (page === "calendar") return <LegislativeCalendar {...authProps} />;
  if (page === "news") return <NewsPage {...authProps} />;
  if (page === "map") return <DistrictMapPage {...authProps} />;
  if (page === "billtracker") return <BillTrackerPage {...authProps} />;
  if (page === "trackedlegislation") return <TrackedLegislationPage {...authProps} />;
  if (page === "clients") return <ClientPortalPage {...authProps} />;
  if (page === "directory") return <DirectoryPage {...authProps} />;
  if (page === "directoryadmin") return <DirectoryAdmin {...authProps} />;
  return <LegiSIM onNavigate={handleNavigate} />;
}
